import { useEffect, useState } from "react";
import PaymentWithInvoices from "../components/payment/PaymentWithInvoices";
import Loader from "../components/ui/Loader";
import TablePayment from "../components/ui/tables/TablePayment";
import { useButtonStore } from "../stores/components/button";
import { useLoaderStore } from "../stores/components/loader";
import { usePaymentStore } from "../stores/payment";
import { useUtilsStore } from "../stores/components/utils";
import { getPayments, getPaymentsDifferences } from "../api/payments";
import { getSlips } from "../api/slips";
import SlipsWithBank from "../components/payment/SlipsWithBank";
import { formatDateForInput } from "../services/function";
import useModals from "../hooks/useModals";
import BankDeposit from "../components/payment/BankDeposit";
import ExportSage from "../components/payment/ExportSage";
import SEPA from "../components/payment/SEPA";
import { showToastError } from "../services/toast";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { getSortButtonClasses, getSortIcon } from "../utils/sortIcons";
import useSorting from "../contexts/useSorting";
import {
  ActionButton,
  PageHeader,
  PagePanel,
  PageShell,
  PanelCenter,
  PanelToolbar,
  PlusIcon,
  ResultCount,
  SearchField,
  SortBar,
  TabBar,
  ToolbarRow,
} from "../components/ui/page/PageShell";

const TITLE_BY_TAB: Record<string, string> = {
  Encaissements: "Encaissements",
  PaymentDifferences: "Écarts de règlement",
  Bordereaux: "Bordereaux",
};

const ROW_LABEL_BY_TAB: Record<string, [string, string]> = {
  Encaissements: ["encaissement", "encaissements"],
  PaymentDifferences: ["écart", "écarts"],
  Bordereaux: ["bordereau", "bordereaux"],
};

const Payment = () => {
  // Store
  const {
    tabPayment,
    setTabPayment,
    viewOrUpdate,
    payments,
    setPayments,
    dataFilterPayments,
    setDataFilterPayments,
    paymentDifferences,
    setPaymentDifferences,
    dataFilterPaymentDifferences,
    setDataFilterPaymentDifferences,
    slips,
    setSlips,
    dataFilterSlips,
    setDataFilterSlips,
    updatePaymentBool,
    createSlipBool,
    setCreateSlipBool,
  } = usePaymentStore();
  const { isLoading } = useLoaderStore();
  const { createButton, setCreateButton } = useButtonStore();
  const { searchTerm, setSearchTerm } = useUtilsStore();

  // State
  const [loaderChargement, setLoaderChargement] = useState<boolean>(false);

  // Modals
  const { modals, openModals, openModal, closeModal } = useModals();

  // Permet de savoir si on est en train de créer ou de modifier
  const isCreatingOrUpdating = createButton || viewOrUpdate;


  useEffect(() => {
    if (tabPayment === "Encaissements") {
      setLoaderChargement(true);
      getPayments()
        .then((response) => {
          setPayments(response.data);
          setLoaderChargement(false);
        })
        .catch((err) => {
          setLoaderChargement(false);
          showToastError(err.response.data.message)
        });
    } else if (tabPayment === "PaymentDifferences") {
      setLoaderChargement(true);
      getPaymentsDifferences()
        .then((response) => {
          setPaymentDifferences(response.data);
          setLoaderChargement(false);
        })
        .catch((err) => {
          setLoaderChargement(false);
          showToastError(err.response.data.message)
        });
    } else {
      setLoaderChargement(true);
      getSlips()
        .then((response) => {
          setSlips(response.data);
          setLoaderChargement(false);
        })
        .catch((err) => {
          setLoaderChargement(false);
          showToastError(err.response.data.message)
        });
    }
  }, [tabPayment, createButton, updatePaymentBool]);

  useEffect(() => {
    let data = [];
    if (tabPayment === "Encaissements") {
      data = payments?.length > 0 ? [...payments] : [...dataFilterPayments];

      if (searchTerm !== "") {
        data = data.filter(
          (item) =>
            item["nameCustomerFile"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["namePaymentMethod"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["balance"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["amount"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["date"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
        );

        setDataFilterPayments(data);
      } else {

        setDataFilterPayments(data);
      }
    } else if (tabPayment === "PaymentDifferences") {
      data =
        paymentDifferences?.length > 0
          ? [...paymentDifferences]
          : [...dataFilterPaymentDifferences];
      if (searchTerm !== "") {
        data = data.filter(
          (item) =>
            item["nameCustomerFile"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["namePaymentMethod"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["balance"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["amount"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["date"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
        );

        setDataFilterPaymentDifferences(data);
      } else {
        setDataFilterPaymentDifferences(paymentDifferences);
      }
    } else if (tabPayment === "Bordereaux") {
      data = slips?.length > 0 ? [...slips] : [...dataFilterSlips];
      if (searchTerm !== "") {
        data = data.filter(
          (item) =>
            item["nameBank"]
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            item["date"]?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item["amount"]
              ?.toString()
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase())
        );

        setDataFilterSlips(data);
      } else {
        const sortData = slips?.sort((a, b) => {
          const dateA = new Date(formatDateForInput(a.date)).getTime();
          const dateB = new Date(formatDateForInput(b.date)).getTime();
          return dateB - dateA; // Tri décroissant
        });
        setDataFilterSlips(sortData);
      }
    }
  }, [paymentDifferences, searchTerm, payments, slips]);

  // Récupère les encaissements et les écarts de règlement
  const [sortedPayments, handleSortPayments, paymentsSortField, paymentsSortDirection] = useSorting(dataFilterPayments);

  const activeData =
    tabPayment === "Encaissements"
      ? sortedPayments
      : tabPayment === "PaymentDifferences"
        ? dataFilterPaymentDifferences
        : dataFilterSlips;
  const [singular, plural] = ROW_LABEL_BY_TAB[tabPayment] ?? [
    "élément",
    "éléments",
  ];

  return (
    <PageShell>
      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null
      )}

      {createSlipBool && (
        <SlipsWithBank closeModal={() => setCreateSlipBool(!createSlipBool)} />
      )}

      {isCreatingOrUpdating && tabPayment === "Encaissements" ? (
        <PagePanel>
          <PaymentWithInvoices />
        </PagePanel>
      ) : (
        <>
          <PageHeader
            eyebrow="Trésorerie"
            title={TITLE_BY_TAB[tabPayment] ?? "Trésorerie"}
            actions={
              <>
                {tabPayment === "Encaissements" && (
                  <>
                    <ActionButton
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(
                          "sepa",
                          <SEPA closeModal={() => closeModal("sepa")} />
                        );
                      }}
                    >
                      Remise SEPA
                    </ActionButton>
                    <ActionButton
                      variant="primary"
                      icon={<PlusIcon />}
                      onClick={() => setCreateButton(!createButton)}
                    >
                      Créer un encaissement
                    </ActionButton>
                  </>
                )}

                {tabPayment === "Bordereaux" && (
                  <>
                    <ActionButton
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(
                          "exportSage",
                          <ExportSage
                            closeModal={() => closeModal("exportSage")}
                          />
                        );
                      }}
                    >
                      Export Sage
                    </ActionButton>
                    <ActionButton
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(
                          "bankDepositModal",
                          <BankDeposit
                            closeModal={() => closeModal("bankDepositModal")}
                          />
                        );
                      }}
                    >
                      Remise de banque
                    </ActionButton>
                    <ActionButton
                      variant="primary"
                      icon={<PlusIcon />}
                      onClick={() => setCreateSlipBool(!createSlipBool)}
                    >
                      Créer bordereau
                    </ActionButton>
                  </>
                )}
              </>
            }
          />

          <PagePanel>
            <PanelToolbar>
              <ToolbarRow>
                <TabBar
                  tabs={[
                    { id: "Encaissements", label: "Encaissements" },
                    { id: "PaymentDifferences", label: "Écarts de règlement" },
                    { id: "Bordereaux", label: "Bordereaux" },
                  ]}
                  value={tabPayment}
                  onChange={(id) => setTabPayment(id)}
                  className="lg:w-[34rem]"
                />
                <ResultCount
                  count={activeData?.length ?? 0}
                  singular={singular}
                  plural={plural}
                  filtered={searchTerm !== ""}
                />
              </ToolbarRow>

              <ToolbarRow>
                <SearchField
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Rechercher un dossier, une date, un montant…"
                />

                {tabPayment === "Encaissements" && (
                  <SortBar>
                    <button
                      onClick={() => handleSortPayments("nameCustomerFile")}
                      className={getSortButtonClasses(
                        "nameCustomerFile",
                        paymentsSortField
                      )}
                    >
                      <span>Dossier</span>
                      {getSortIcon(
                        "nameCustomerFile",
                        paymentsSortField,
                        paymentsSortDirection
                      )}
                    </button>
                    <button
                      onClick={() => handleSortPayments("date")}
                      className={getSortButtonClasses("date", paymentsSortField)}
                    >
                      <span>Date</span>
                      {getSortIcon(
                        "date",
                        paymentsSortField,
                        paymentsSortDirection
                      )}
                    </button>
                    <button
                      onClick={() => handleSortPayments("amount")}
                      className={getSortButtonClasses(
                        "amount",
                        paymentsSortField
                      )}
                    >
                      <span>Montant TTC</span>
                      {getSortIcon(
                        "amount",
                        paymentsSortField,
                        paymentsSortDirection
                      )}
                    </button>
                    <button
                      onClick={() => handleSortPayments("balance")}
                      className={getSortButtonClasses(
                        "balance",
                        paymentsSortField
                      )}
                    >
                      <span>Solde</span>
                      {getSortIcon(
                        "balance",
                        paymentsSortField,
                        paymentsSortDirection
                      )}
                    </button>
                  </SortBar>
                )}
              </ToolbarRow>
            </PanelToolbar>

            {loaderChargement ? (
              <PanelCenter>
                <LoadingSpinner size="large" text="Chargement des données..." />
              </PanelCenter>
            ) : isLoading ? (
              // Affichage du loader a la création
              <PanelCenter>
                <Loader />
              </PanelCenter>
            ) : tabPayment === "Encaissements" ? (
              <TablePayment
                columns={[
                  {
                    header: "Statut",
                    accessor: "idSlip",
                  },
                  {
                    header: "Dossier",
                    accessor: "nameCustomerFile",
                  },
                  {
                    header: "Paiement",
                    accessor: "namePaymentMethod",
                  },
                  { header: "Date", accessor: "date" },
                  { header: "Bordereau", accessor: "nameSlip" },
                  { header: "TTC", accessor: "amount" },
                  { header: "Solde", accessor: "balance" },
                ]}
                data={sortedPayments}
              />
            ) : tabPayment === "PaymentDifferences" ? (
              <TablePayment
                columns={[
                  {
                    header: "Catégorie",
                    accessor: "namePaymentDifferenceCategory",
                  },
                  {
                    header: "Dossier",
                    accessor: "nameCustomerFile",
                  },
                  { header: "Date", accessor: "date" },
                  {
                    header: "TTC Factures",
                    accessor: "totalInvoices",
                  },
                  {
                    header: "Montant TTC ",
                    accessor: "amount",
                  },
                ]}
                data={dataFilterPaymentDifferences}
              />
            ) : tabPayment === "Bordereaux" ? (
              <TablePayment
                columns={[
                  {
                    header: "Banque",
                    accessor: "nameBank",
                  },
                  {
                    header: "Guichet",
                    accessor: "tellerCode",
                  },
                  { header: "Date", accessor: "date" },
                  {
                    header: "Nombre d'écriture",
                    accessor: "numbersOfPayments",
                  },
                  {
                    header: "Montant TTC",
                    accessor: "amount",
                  },
                ]}
                data={dataFilterSlips}
              />
            ) : null}
          </PagePanel>
        </>
      )}
    </PageShell>
  );
};

export default Payment;
